/* ────────────────────────────────────────────────────────────────────────
   Notification chime.

   A short, pleasant two-note chime synthesised with the Web Audio API — no
   binary asset to ship or cache-bust. Used for FOREGROUND push notifications
   (onMessage); background pushes already play the OS notification sound via the
   service worker, so we don't double up there.

   Autoplay note: browsers start an AudioContext "suspended" until a user
   gesture. `primeNotificationSound()` should be called once from the first
   click/keydown so later (gesture-less) notification sounds can play.
   ──────────────────────────────────────────────────────────────────────── */

// localStorage flag so a future "mute notifications" toggle is a one-liner.
export const NOTIFICATION_SOUND_MUTE_STORAGE_KEY = "notificationSoundMuted";

// ── Chime tuning (all times in seconds, frequencies in Hz) ───────────────────
// Two rising notes an octave-ish apart give a gentle "ding-dong" rather than a
// harsh single beep.
const FIRST_NOTE_FREQUENCY_HZ = 880.0; // A5 — the lower "ding"
const SECOND_NOTE_FREQUENCY_HZ = 1174.66; // ~D6 — the higher "dong"

const FIRST_NOTE_START_SECONDS = 0; // first note plays immediately
const FIRST_NOTE_DURATION_SECONDS = 0.18;
const SECOND_NOTE_START_SECONDS = 0.12; // overlaps slightly for a smooth blend
const SECOND_NOTE_DURATION_SECONDS = 0.24;

// Gain envelope: ramp from near-silent up to a soft peak, then back down, so each
// note reads as a chime with a soft attack/release instead of a click.
const SILENT_GAIN = 0.0001; // exponential ramps can't target exactly 0
const PEAK_GAIN = 0.18;
const ATTACK_SECONDS = 0.02; // time to reach PEAK_GAIN after a note starts

/** A single synthesised note in the chime. */
interface Note {
  /** Pitch in Hertz (higher = higher pitch). */
  frequencyHz: number;
  /** When the note starts, in seconds relative to the chime's start. */
  startSeconds: number;
  /** How long the note sounds, in seconds. */
  durationSeconds: number;
}

const CHIME_NOTES: Note[] = [
  {
    frequencyHz: FIRST_NOTE_FREQUENCY_HZ,
    startSeconds: FIRST_NOTE_START_SECONDS,
    durationSeconds: FIRST_NOTE_DURATION_SECONDS,
  },
  {
    frequencyHz: SECOND_NOTE_FREQUENCY_HZ,
    startSeconds: SECOND_NOTE_START_SECONDS,
    durationSeconds: SECOND_NOTE_DURATION_SECONDS,
  },
];

// Single shared AudioContext — created lazily so we don't spin one up until it's
// actually needed (and so we can reuse the unlocked instance across plays).
let cachedAudioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  // Safari still exposes it only under the webkit prefix.
  const AudioContextCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return null;
  if (!cachedAudioContext) cachedAudioContext = new AudioContextCtor();
  return cachedAudioContext;
};

const isSoundMuted = (): boolean => {
  try {
    return localStorage.getItem(NOTIFICATION_SOUND_MUTE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Unlocks audio playback. Call once from the first user gesture (click/keydown)
 * so a notification arriving later can play without its own gesture.
 */
export const primeNotificationSound = (): void => {
  const audioContext = getAudioContext();
  if (audioContext && audioContext.state === "suspended") {
    audioContext.resume().catch(() => {
      /* ignore — stays suspended until the next gesture */
    });
  }
};

/**
 * Plays a short two-note chime. No-ops silently if audio is unavailable, muted,
 * or still locked by the browser's autoplay policy.
 */
export const playNotificationSound = (): void => {
  try {
    if (isSoundMuted()) return;

    const audioContext = getAudioContext();
    if (!audioContext) return;
    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {
        /* ignore — the play below simply won't be audible */
      });
    }

    const now = audioContext.currentTime;

    CHIME_NOTES.forEach(({ frequencyHz, startSeconds, durationSeconds }) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequencyHz;

      const startTime = now + startSeconds;
      const endTime = startTime + durationSeconds;

      // Soft attack up to the peak, then exponential release down to near-silent.
      gainNode.gain.setValueAtTime(SILENT_GAIN, startTime);
      gainNode.gain.exponentialRampToValueAtTime(PEAK_GAIN, startTime + ATTACK_SECONDS);
      gainNode.gain.exponentialRampToValueAtTime(SILENT_GAIN, endTime);

      oscillator.connect(gainNode).connect(audioContext.destination);
      oscillator.start(startTime);
      oscillator.stop(endTime);
    });
  } catch (err) {
    console.error("Failed to play notification sound:", err);
  }
};
